import React, { useCallback } from "react";
import { BaseModal, IModalInstance, ModalHeader, modalService } from "@whatslly/ui-components/modals";
import styles from "./PurchacePhoneModal.module.scss";
import { useTranslation } from "react-i18next";
import { inject, injectable } from "inversify";
import { COUNTRIES } from "@whatslly/commons/phoneUtils/countries";
import { computed, makeObservable, observable } from "mobx";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { IOrganizationStore } from "../../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../../services/IOrganizationStore";
import type { TwilioAccount } from "@whatslly/types/twilio/TwilioGetPhones";
import { TwilioPhone } from "@whatslly/types/twilio/TwilioGetPhones";
import { getJSON, putJSON } from "@whatslly/commons/apiUtils";
import { GetAvailablePhonesResponse, TwilioUpdatePhoneResponse } from "@whatslly/types/twilio/TwilioManagePhones";
import { fromPromise } from "mobx-utils";
import { observer, useLocalStore } from "mobx-react-lite";
import { Table } from "@whatslly/ui-components/v2/Tables/Table/Table";
import { Button } from "@whatslly/ui-components/v2/Button";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { getTwilioCapabilities } from "../twilioUtils";

const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({ label: `${country.flag} ${country.country}`, value: country.ISOCode }));

@injectable()
class PurchacePhoneStore {
  private countryCode = "BR";
  accountSid: string | undefined;
  purchased = false;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    private accounts: TwilioAccount[]
  ) {
    makeObservable<PurchacePhoneStore, "countryCode">(this, {
      countryCode: observable,
      accountSid: observable,
      purchased: observable,
      country: computed,
      avilablePhones: computed,
    });
    this.accountSid = accounts[0]?.AccountSid;
  }

  get country() {
    return COUNTRY_OPTIONS.find((country) => country.value === this.countryCode);
  }

  private async getPhones(accountSid: string, countryCode: string) {
    const orgId = await this.orgStore.orgId;
    if (!orgId || !accountSid) {
      return [];
    }
    const data: GetAvailablePhonesResponse = await getJSON({
      urlSuffix: `/integrations/twilio/phones/${accountSid}?orgId=${orgId}&country=${countryCode}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    })
      .then((res) => res.json())
      .catch(() => ({ success: false }));
    if (!data.success) {
      return [];
    }
    const { prices, phones, price_unit } = data;
    const formatter = new Intl.NumberFormat(undefined, { style: "currency", currency: price_unit });
    return phones.map((phone) => ({ ...phone, price: formatter.format(Number(prices.find((price) => price.type === phone.type)?.price ?? 0)) }));
  }

  get avilablePhones() {
    return fromPromise(this.getPhones(this.accountSid ?? "", this.countryCode));
  }

  async purchase(t: any, phoneNumber: string) {
    const orgId = await this.orgStore.orgId;
    if (!orgId || !this.accountSid) {
      return [];
    }

    const confirm = await modalService.openModal(ConfirmModal, {
      title: t("twilio_settings_page.purchase_phone.confirm_purchase"),
      body: t("twilio_settings_page.purchase_phone.confirm_purchase_body", { phoneNumber }),
    }).promise;
    if (!confirm?.result) {
      return;
    }

    const data: TwilioUpdatePhoneResponse = await putJSON({
      urlSuffix: `/integrations/twilio/phones/${this.accountSid}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ orgId: await this.orgStore.orgId, phoneNumber }),
      pod: await this.orgStore.wPod,
    })
      .then((res) => res.json())
      .catch(() => ({ success: false }));
    this.purchased = this.purchased || data.success;
    if (data.success) {
      modalService.openModal(Toast, { text: t("twilio_settings_page.purchase_phone.phone_purchased_successfully") });
    } else {
      if (data.error?.includes("AddressSid")) {
        modalService.openModal(Toast, {
          text: t("twilio_settings_page.purchase_phone.phone_purchased_failed", {
            error: t("twilio_settings_page.no_address_error_message"),
          }),
          type: "error",
        });
        return;
      }
      modalService.openModal(Toast, { text: t("twilio_settings_page.purchase_phone.phone_purchased_failed", { error: data.error }), type: "error" });
    }
  }
}

interface PurchacePhoneModalProps extends IModalInstance<boolean> {
  accounts: TwilioAccount[];
}

export const PurchacePhoneModal: React.FC<PurchacePhoneModalProps> = observer(({ closeModal, accounts }) => {
  const { t } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const orgStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const store = useLocalStore(() => new PurchacePhoneStore(userStore, orgStore, accounts));

  const close = useCallback(() => closeModal(store.purchased), [store.purchased, closeModal]);

  return (
    <BaseModal
      header={<ModalHeader closeModal={close}>{t("twilio_settings_page.add_phone")}</ModalHeader>}
      closeModal={close}
      className={styles.modal}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <div className={styles.table}>
        <Table
          loading={store.avilablePhones.state === "pending"}
          items={store.avilablePhones.case({ pending: () => [], rejected: () => [], fulfilled: (phones) => phones })}
          columns={[
            {
              title: t("twilio_settings_page.cols.phone"),
              keyInsideItem: `friendlyName`,
            },
            {
              title: t("twilio_settings_page.cols.type"),
              keyInsideItem: `type`,
            },
            {
              title: t("twilio_settings_page.cols.capabilities"),
              keyInsideItem: `capabilities`,
              type: `tags`,
              "data-testid": "twilio-capabilities",
              getValue: (item) => getTwilioCapabilities(t, item as Partial<TwilioPhone>),
            },
            {
              title: t("twilio_settings_page.cols.price"),
              keyInsideItem: `price`,
            },
            {
              title: t("twilio_settings_page.purchase_phone.purchase"),
              keyInsideItem: `phoneNumber`,
              getValue: (phone) => (
                <div>
                  <Button testId={"purchase_phone_number"} paddingInline={`small`} onClick={() => store.purchase(t, phone.phoneNumber)}>
                    {t("twilio_settings_page.purchase_phone.purchase")}
                  </Button>
                </div>
              ),
            },
          ]}
          emptyStateNode={<></>}
        />
      </div>
    </BaseModal>
  );
});
